import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { ReassignResponsibleDrawer } from "@/components/client360/ReassignResponsibleDrawer";
import type { SupervisorAttentionItem, SupervisorAttentionQueue as Queue } from "@/api/types";

/**
 * S2 Block 2 — «Потребує уваги» (exception queue, mockup v2). Exception-only rows,
 * each with ONE navigational primary CTA (entity-specific). C3: reassign → S3 and
 * «Нагадати» → arming are HIDDEN here — 0 dead/inert CTA in P0 (§0/§20).
 */

const CHIPS: Array<{ key: string; label: string; types: string[] | null }> = [
  { key: "all", label: "Усі", types: null },
  { key: "tasks", label: "Задачі", types: ["task_overdue"] },
  { key: "meetings", label: "Зустрічі", types: ["meeting_report_overdue"] },
  { key: "profiles", label: "Профілі", types: ["profile_review_pending"] },
  { key: "requests", label: "Запити", types: ["internal_request_sla_breach"] },
  { key: "clients", label: "Клієнти", types: ["client_without_next_action"] },
  { key: "ar", label: "AR", types: ["ar_risk_client"] },
];

function sevClass(severity: number): string {
  if (severity <= 1) return "bg-[hsl(var(--destructive))]";
  if (severity <= 3) return "bg-[hsl(var(--warning-foreground))]";
  return "bg-[hsl(var(--info-foreground))]";
}

function ageClass(severity: number): string {
  if (severity <= 1) return "text-[hsl(var(--destructive))]";
  if (severity <= 3) return "text-[hsl(var(--warning-foreground))]";
  return "text-muted-foreground";
}

function ctaFor(item: SupervisorAttentionItem): { label: string; route: string } {
  switch (item.type) {
    case "task_overdue":
      return { label: "Відкрити задачу", route: `/tasks?open=${item.entityId}` };
    case "meeting_report_overdue":
      // D-137 W5a: read drawer (supervisor read-rescue), not the manager fill wizard.
      return { label: "Відкрити зустріч", route: `/meetings?open=${item.entityId}` };
    case "internal_request_sla_breach":
      return { label: "Відкрити запит", route: `/requests?open=${item.entityId}` };
    case "ar_risk_client":
      return { label: "Картка клієнта", route: `/clients/${item.entityId}` };
    case "profile_review_pending":
      return item.holdingId
        ? { label: "Картка клієнта", route: `/clients/${item.holdingId}` }
        : { label: "На перевірку", route: "/needs-review?scope=team" };
    case "client_without_next_action":
      return { label: "Картка клієнта", route: "/clients?scope=team&dataQuality=no_next_action" };
    default:
      return { label: "Огляд", route: "/clients?scope=team" };
  }
}

function ageLabel(item: SupervisorAttentionItem): string {
  if (item.ageDays === null || item.ageDays === undefined) return "—";
  if (item.ageDays === 0) return "сьогодні";
  if (item.ageDays === 1) return "1 день";
  return `${item.ageDays} дн.`;
}

export function SupervisorAttentionQueue({ queue }: { queue: Queue }) {
  const navigate = useNavigate();
  const [chip, setChip] = useState("all");
  // S4 — reassign drawer (functional CTA on meeting rows; scope-checked backend).
  const [reassign, setReassign] = useState<{ id: string; holdingId: string } | null>(null);

  const filtered = useMemo(() => {
    const def = CHIPS.find((c) => c.key === chip);
    if (!def || def.types === null) return queue.items;
    return queue.items.filter((i) => def.types!.includes(i.type));
  }, [queue.items, chip]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setChip(c.key)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              chip === c.key ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:bg-muted",
            )}
          >
            {c.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border bg-card py-12 text-center">
          <CheckCircle2 className="mx-auto h-7 w-7 text-[hsl(var(--success-foreground))]" />
          <div className="mt-2 font-semibold">Немає критичних дій по команді</div>
          <div className="mt-1 text-xs text-muted-foreground">Усі задачі, звіти й запити в межах SLA.</div>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/clients?scope=team")}>
            Огляд команди →
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-card">
          {filtered.map((item, idx) => {
            const cta = ctaFor(item);
            return (
              <div
                key={`${item.type}:${item.entityId}:${idx}`}
                className="flex items-center gap-3.5 border-b px-4 py-3 last:border-b-0"
              >
                <span className={cn("h-2 w-2 flex-none rounded-full", sevClass(item.severity))} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{item.reason}</div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {item.clientName && (
                      <>клієнт <span className="font-semibold text-foreground">{item.clientName}</span></>
                    )}
                    {item.responsibleManager && (
                      <> · відповідальний <span className="font-semibold text-foreground">{item.responsibleManager}</span></>
                    )}
                  </div>
                </div>
                <span className={cn("whitespace-nowrap text-xs font-semibold tabular-nums", ageClass(item.severity))}>
                  {ageLabel(item)}
                </span>
                {item.type === "meeting_report_overdue" && item.holdingId && (
                  <Button variant="outline" size="sm" onClick={() => setReassign({ id: item.entityId, holdingId: item.holdingId! })}>
                    Перепризначити
                  </Button>
                )}
                <Button size="sm" onClick={() => navigate(cta.route)}>
                  {cta.label}
                </Button>
              </div>
            );
          })}
          {queue.capped && (
            <div className="px-4 py-2 text-center text-[11px] text-muted-foreground">
              Показано перші {filtered.length} · всього {queue.total}
            </div>
          )}
        </div>
      )}

      {reassign && (
        <ReassignResponsibleDrawer
          interactionId={reassign.id}
          holdingId={reassign.holdingId}
          onClose={() => setReassign(null)}
        />
      )}
    </div>
  );
}
